import { useState } from 'react';
import { Search, X, Clock, TrendingUp } from 'lucide-react';

interface SearchBarProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
}

const SearchBar = ({ searchTerm, onSearchChange }: SearchBarProps) => {
  const [isFocused, setIsFocused] = useState(false);

  // Mock recent searches - in real implementation, this would come from local storage
  const recentSearches = ['Gym memberships', 'Restaurant deals', 'Electronics'];

  // Mock suggested perks
  const suggestedPerks = [
    'Wellness & Fitness',
    'Food & Dining',
    'Travel Discounts',
    'Tech & Electronics'
  ];

  const handleClearSearch = () => {
    onSearchChange('');
  };

  return (
    <div className="relative">
      <div className={`relative transition-all duration-200 ${isFocused ? 'ring-2 ring-primary' : ''} rounded-lg`}>
        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
          <Search className="w-5 h-5 text-gray-400" />
        </div>
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setTimeout(() => setIsFocused(false), 200)}
          placeholder="Search for perks, merchants, or categories..."
          className="w-full pl-12 pr-12 py-4 bg-card border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-gray-900 placeholder-gray-500"
        />
        {searchTerm && (
          <button
            onClick={handleClearSearch}
            className="absolute inset-y-0 right-0 pr-4 flex items-center"
          >
            <X className="w-5 h-5 text-gray-400 hover:text-gray-600" />
          </button>
        )}
      </div>

      {/* Search Suggestions Dropdown */}
      {isFocused && !searchTerm && (
        <div className="absolute z-10 w-full mt-2 bg-card rounded-lg shadow-lg border border-border overflow-hidden">
          {/* Recent Searches */}
          {recentSearches.length > 0 && (
            <div className="p-4 border-b border-border">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Recent Searches
                </span>
              </div>
              <div className="space-y-2">
                {recentSearches.map((search, index) => (
                  <button
                    key={index}
                    onClick={() => onSearchChange(search)}
                    className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors duration-150"
                  >
                    <Clock className="w-4 h-4 text-gray-400 mr-3" />
                    {search}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Suggested Perks */}
          <div className="p-4">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 block">
              Suggested Categories
            </span>
            <div className="flex flex-wrap gap-2">
              {suggestedPerks.map((perk, index) => (
                <button
                  key={index}
                  onClick={() => onSearchChange(perk)}
                  className="inline-flex items-center px-3 py-1.5 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors duration-150"
                >
                  <TrendingUp className="w-3.5 h-3.5 mr-1.5" />
                  {perk}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchBar;
